import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/react';
import { getSignedInUser } from '../services/user.service';
import { useOktaAuth } from '@okta/okta-react';
import { getConfig } from '../services/config.service';

const UserContext = createContext();

// User Type IDs from the database
const USER_TYPE_MOBILE_ONLY = 1;

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isUserVerified, setIsUserVerified] = useState(false);
    const { oktaAuth, authState } = useOktaAuth();
    const hasFetchedRef = useRef(false);
    const identityKeyRef = useRef(null);

    //const [authState, setAuthState] = useState(null);
    const [isProfileComplete, setIsProfileComplete] = useState(false);

    // Determine auth status - null/undefined means still loading
    const isAuthenticated = authState?.isAuthenticated;
    const isAuthLoading = authState === null || authState === undefined;
    const identityKey =
        authState?.accessToken?.claims?.uid ||
        authState?.accessToken?.claims?.sub ||
        authState?.idToken?.claims?.sub ||
        authState?.idToken?.claims?.email ||
        null;

    //Get current User and verify access
    useEffect(() => {
        // Wait for authState to be loaded
        if (isAuthLoading) {
            return;
        }

        // If not authenticated, mark as verified (no user to check)
        if (!isAuthenticated) {
            hasFetchedRef.current = false;
            identityKeyRef.current = null;
            setUser(null);
            setIsUserVerified(true);
            return;
        }

        // If the identity changes (different user), re-verify.
        if (identityKeyRef.current !== identityKey) {
            identityKeyRef.current = identityKey;
            hasFetchedRef.current = false;
            setUser(null);
            setIsUserVerified(false);
        }

        // User is authenticated, fetch and verify their access
        async function fetchAndVerifyUser() {
            try {
                // Already verified/fetched for this auth session; never block navigation.
                if (hasFetchedRef.current) {
                    setIsUserVerified(true);
                    return;
                }

                setIsUserVerified(false);
                hasFetchedRef.current = true;

                const response = await getSignedInUser();
                
                // Check if we got a valid user response
                if (response && typeof response.userTypeId === 'number') {
                    // Check if user is mobile-only (User_Type_ID = 1)
                    // Mobile-only users are not allowed to access the web application
                    if (response.userTypeId === USER_TYPE_MOBILE_ONLY) {
                        // Store error in sessionStorage before signing out
                        sessionStorage.setItem('safepath_login_error', 'mobile_only');
                        // Sign out using default redirect (avoids Okta URI validation issues)
                        oktaAuth.signOut();
                        return; // Don't set verified, we're signing out
                    }
                    setUser(response);

                    // Identify user in LogRocket for session tracking
                    if (getConfig().logRocketAppId && response.userId) {
                        LogRocket.identify(String(response.userId), {
                            name: `${response.firstName || ''} ${response.lastName || ''}`.trim() || undefined,
                            email: response.email || undefined,
                            organizationId: response.organizationId || undefined,
                        });
                    }

                    // Identify the user in Sentry (independent of LogRocket).
                    if (response.userId) {
                        Sentry.setUser({
                            id: String(response.userId),
                            email: response.email || undefined,
                            segment: response.organizationId ? String(response.organizationId) : undefined,
                        });
                    }
                } else {
                    // No valid user data - could be API error or empty response
                }

                // Either way, don't keep the user stuck behind a gate.
                setIsUserVerified(true);
            } catch (error) {
                // Previously swallowed silently. A failure here means user
                // verification broke (auth/API) — surface it instead of hiding it.
                Sentry.captureException(error);
                setIsUserVerified(true); // Allow through on error to not block the app
            }
        }

        fetchAndVerifyUser();
    // Deliberately do NOT depend on oktaAuth object identity here; we only re-verify on auth/identity changes.
    }, [isAuthLoading, isAuthenticated, identityKey]);

    // Is user able to see Admin-restricted areas?
    const roles = authState?.accessToken?.claims?.roles || [];
    const groups = authState?.accessToken?.claims?.groups || [];
    const adminPrivilege =
        roles.includes("Gov_Admin") ||
        roles.includes("SuperAdmin") ||
        roles.includes("SafePathOrgAdmin") ||
        groups.includes("SafePathOrgAdmin");

    // Check if user is SuperAdmin
    const isSuperAdmin =
        authState?.accessToken?.claims?.roles?.includes("SuperAdmin") ||
        user?.roles?.some(role => role.roleName === "SuperAdmin");

    // Check if user is Org Admin (SafePathOrgAdmin or SuperAdmin, but NOT Gov_Admin)
    // Used for restricting certain actions like deleting defendants
    const isOrgAdmin =
        roles.includes("SafePathOrgAdmin") ||
        groups.includes("SafePathOrgAdmin") ||
        roles.includes("SuperAdmin");

    // Get organization ID from user
    const organizationId = user?.organizationId || null;

    return (
        <UserContext.Provider
            value={{
                user,
                //authState,
                adminPrivilege,
                isSuperAdmin,
                isOrgAdmin,
                organizationId,
                isProfileComplete,      // <-- Expose the boolean
                setIsProfileComplete,   // <-- Expose the setter
                isUserVerified,
                //signIn,
                //signOut,
            }}
        >
            {children}
        </UserContext.Provider>
    )
};

export const useUser = () => useContext(UserContext);
